/**
 * Whether to adopt a token supplied by an EXTERNAL party (currently: the native host).
 *
 * The rule this file exists to enforce is "never downgrade". A token arriving from
 * outside CF is not automatically better than the one CF already holds, and adopting it
 * blindly can destroy a working session:
 *
 *   CF's own `useAuth.refresh()` rotates the token via `/auth/refresh` → `startSession()`,
 *   so localStorage can hold a token valid for another 30 minutes. Native's AsyncStorage
 *   is an INDEPENDENT store: `checkAndRefreshToken()` only refreshes inside the 1h window
 *   and the `WEBVIEW_AUTH_TOKEN` sync message can be missed, so native's copy may be
 *   older — and when its own refresh fails it replies with that copy anyway
 *   (`sendAuthToken` logs "sending existing token anyway"; `getAuthToken` is a raw
 *   storage read with no expiry check, mobile-app authService.ts:204-211).
 *   Writing that over CF's JWT logs a paying customer out of a working session.
 *
 * Applies to the WebView reply path specifically. `Http.refreshTokenViaApi` also calls
 * `AuthUtils.setJwt` but its token is the API's own fresh refresh response, so there is
 * no older-copy hazard there; it is deliberately left alone rather than routed through
 * this check, which would add a clock-skew failure mode to the main refresh path.
 */

/**
 * Margin by which a token must out-live "now" to be worth acting on.
 *
 * A bare `now > expires` treats a token with milliseconds of life left as good. Adopting
 * one and reloading on it is self-defeating: `window.location.replace` takes on the order
 * of a second, and the new document initialises `isLoggedIn` from
 * `!AuthUtils.isExpired()`, so the user lands on the login form having spent a navigation
 * to get there. Sized to the native reply window, because that is the same round-trip
 * everything else here is paced to.
 */
export const TOKEN_FRESHNESS_MARGIN_MS = 5000;

/**
 * Is this expiry usable — i.e. finite, and far enough in the future to act on?
 *
 * Non-finite counts as unusable, which is NOT pedantry: `AuthUtils.make` derives
 * `expires` from `decode(token).exp * 1000`, so a token with no `exp` claim yields
 * `undefined * 1000` → NaN. `Date.now() > NaN` is FALSE, so an undatable token would
 * otherwise read as perfectly fresh. (`AuthUtils.isExpired` has that same NaN behaviour,
 * which is why this does not delegate to it.)
 */
export const isTokenUsable = (
  expires: number,
  now: number,
  marginMs: number = TOKEN_FRESHNESS_MARGIN_MS,
): boolean => Number.isFinite(expires) && expires - now > marginMs;

export interface TokenAdoptionInput {
  /** Expiry of the token just handed to us. */
  incomingExpires: number;
  /** Expiry of the token CF currently holds. NaN/0 when there is none. */
  currentExpires: number;
  now: number;
  marginMs?: number;
}

/**
 * Adopt the incoming token only if it is usable AND not a downgrade.
 *
 * "Not a downgrade" is `incomingExpires > currentExpires`, with one deliberate exception:
 * when what we currently hold is itself unusable there is nothing to protect, so any
 * usable incoming token is an improvement. That exception is what keeps the recovery path
 * working — the whole point of asking native for a token is that ours has gone bad.
 */
export const shouldAdoptToken = ({
  incomingExpires,
  currentExpires,
  now,
  marginMs = TOKEN_FRESHNESS_MARGIN_MS,
}: TokenAdoptionInput): boolean => {
  if (!isTokenUsable(incomingExpires, now, marginMs)) return false;
  if (!isTokenUsable(currentExpires, now, marginMs)) return true;
  return incomingExpires > currentExpires;
};
